/* eslint-disable import/extensions */
import {
  IOnboardingPartials,
  IStakeholdersPartials,
  MxOnboardingRequest,
} from '@pages/onboarding/Colombia/types';
import {
  IStakeholderRequest,
  IStakeholderResponse,
} from '@pages/onboarding/types/newStakeholderResponse';
import { ForwardPayload } from '@pages/payables/hooks/useForward.type';
import {
  CreateContactPayload,
  SwiftCountriesResult,
} from '@/pages/clients/AddContactForm/types';
import { PayloadData } from '../../components/payables/PaymentModal/types';
import { Language } from '../../configs/language';
import client from '../axios';
import { UploadRequestFiles } from '../dashboard/types';
import documentsSvc from '../documents/documentsSvc';
import { IUploadDocuments } from '../documents/types';
import { QueryParams } from '../types';
import {
  BalanceResponse,
  CombinedBalanceResponse,
  GenerateTransfer,
  GetContactCurrencyRequest,
  GetContactCurrencyResponse,
  IBalancesResponse,
  IClientModal,
  IClientModalResponse,
  ICreateStakeholderV2,
  IPagination,
  IPayablePagination,
  IPaymentsBalanceResponse,
  IUploadDocumentsOnboarding,
  MexicoOnboardingGet,
  PaymentDetailResponse,
  UploadFileResponse,
  UploadStaStakeHolders,
  WalletConversion,
  WalletResponse,
} from './types';

const API_PAGINATION_VERSION = 2;

interface IGetClientsRequest {
  params?: QueryParams;
  modal?: boolean;
  page?: number;
}

interface IGetClientsSwiftRequest extends IGetClientsRequest {
  enterpriseId?: string;
}

const sortByOrigin = (data: IClientModal[]): IClientModal[] =>
  data.sort((a, b) => {
    const fa = a.origin.toLowerCase();
    const fb = b.origin.toLowerCase();

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  });

const fixClientsResponse = (data: IClientModalResponse) => {
  const clientData: IClientModal[] = [];
  const clientPayableData: IClientModal[] = [];

  const results = data.result?.length ? sortByOrigin(data.result) : [];
  const payables = data.payable?.length ? sortByOrigin(data.payable) : [];

  results.forEach((obj: any) => {
    clientData.push({
      uuid: obj.uuid || '',
      name: obj.name || '',
      email: obj.email || '',
      country: obj.country || '',
      contactId: obj.contactId || '',
      origin: obj.origin || '',
      companyName: obj.company_name || '',
      entityBusiness: obj.entity_business || obj.user_type || '',
      addressLine1: obj.address_line1 || '',
      addressLine2: obj.address_line2 || '',
      currency: obj.currency || '',
      city: obj.city || '',
      state: obj.state || '',
      nickname: obj.nickname || '',
      bankAccount: obj.bank_account || {},
      contactTypeId: obj.contactTypeId || 1,
      country_iso: obj.country_iso || '',
    });
  });
  payables.forEach((obj: any) => {
    clientPayableData.push({
      uuid: obj.uuid || '',
      name: obj.name || '',
      email: obj.email || '',
      country: obj.country || '',
      contactId: obj.contactId || '',
      origin: obj.origin || '',
      companyName: obj.company_name || '',
      entityBusiness: obj.entity_business || obj.user_type || '',
      addressLine1: obj.address_line1 || '',
      addressLine2: obj.address_line2 || '',
      currency: obj.currency || '',
      city: obj.city || '',
      state: obj.state || '',
      nickname: obj.nickname || '',
      bankAccount: obj.bank_account || {},
      contactTypeId: obj.contactTypeId || 1,
      country_iso: obj.country_iso || '',
    });
  });
  return { clientData, clientPayableData };
};

const getClients = async <T>({ params }: IGetClientsRequest): Promise<T> => {
  const { search, page = 1, dateFrom, dateTo } = params || {};
  const paramsUrl = new URLSearchParams();
  paramsUrl.append('page', `${page}`);
  if (search) paramsUrl.append('search', search);
  if (dateFrom) paramsUrl.append('dateFrom', dateFrom);
  if (dateTo) paramsUrl.append('dateTo', dateTo);
  return client.get(`clients/?${paramsUrl.toString()}`);
};

const getContacts = async <T>({
  params,
  enterpriseId,
}: IGetClientsSwiftRequest): Promise<T> => {
  const { search, page = 1, dateFrom, dateTo } = params || {};
  const paramsUrl = new URLSearchParams();
  paramsUrl.append('page', `${page}`);
  if (search) paramsUrl.append('search', search);
  if (dateFrom) paramsUrl.append('dateFrom', dateFrom);
  if (dateTo) paramsUrl.append('dateTo', dateTo);
  return client.get(
    `/enterprise/${enterpriseId}/contacts/?${paramsUrl.toString()}`
  );
};

const getClientsModal = async (): Promise<{
  clientData: IClientModal[];
  clientPayableData: IClientModal[];
}> => {
  try {
    const response = await getClients<IClientModalResponse>({
      modal: true,
    });
    return fixClientsResponse(response);
  } catch (error) {
    console.error(error);
    return { clientData: [], clientPayableData: [] };
  }
};

const createClientFromExisting = async ({
  uuid,
  email,
  name,
}: {
  uuid: string;
  email: string;
  name: string;
}): Promise<any> => {
  const clientData = { uuid, email, name };
  return client.post(`clients/`, clientData, {
    isAuthRedirect: false,
  });
};

const validateContactEmail = async (email: string): Promise<void> =>
  client.get(`/entity/email/${email}`, {
    isAuthRedirect: false,
  });

const searchContactsV2ByEmail = async ({
  enterpriseId,
  search,
}: {
  enterpriseId: string;
  search: string;
}): Promise<IPagination<CreateContactPayload>> =>
  client.get(`/enterprise/${enterpriseId}/contacts/?search=${search}`, {
    isAuthRedirect: false,
  });

const searchContactByEmail = async (
  email: string,
  enterpriseId: string
): Promise<{
  clientData: IClientModal[];
  clientPayableData: IClientModal[];
}> => {
  try {
    const response = await client.get(
      `/clients-detail/?search=${email}&enterprise_id=${enterpriseId}`,
      {
        isAuthRedirect: false,
      }
    );
    return fixClientsResponse(response);
  } catch (error) {
    console.error(error);
    return { clientData: [], clientPayableData: [] };
  }
};

const getBalance = async (
  language: Language = 'ES'
): Promise<BalanceResponse> => client.get(`charts/?lang=${language}`);

const uploadKybDocument = async (): Promise<UploadFileResponse> =>
  client.get(`upload-file/`);

const postUploadKybDocument = async (
  data: UploadRequestFiles
): Promise<UploadFileResponse> =>
  client.post(`upload-file/`, data, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const postNewUploadKybDocument = async (
  data: IUploadDocumentsOnboarding
): Promise<UploadFileResponse> =>
  client.post(`upload-file/`, data, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const getNewUploadKybDocument = async (): Promise<IOnboardingPartials> =>
  client.get(`upload-file/`, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const generateTransfer = async (data: GenerateTransfer | PayloadData) => {
  const { desAmount, ...rest } = data;
  return client.post('generate-transfer/', rest);
};

const generateSwiftTransfer = async (
  data: PayloadData,
  enterpriseId: string
) => {
  const { desAmount, ...rest } = data;
  return client.post(`enterprise/${enterpriseId}/payments/`, rest);
};

const generateScheduleTransfer = async (data: GenerateTransfer) => {
  const { desAmount, ...rest } = data;
  return client.post('generate-transfer/', { ...rest, payment_schedule: true });
};

const getPayables = async ({
  search,
  page = 1,
  dateFrom,
  dateTo,
}: {
  search?: string;
  page?: number;
  dateFrom?: string;
  dateTo?: string;
}): Promise<IPayablePagination> => {
  const params = new URLSearchParams();
  params.append('page', `${page}`);
  params.append('_v', `${API_PAGINATION_VERSION}`);
  if (search) params.append('search', search);
  if (dateFrom) params.append('dateFrom', dateFrom);
  if (dateTo) params.append('dateTo', dateTo);
  return client.get(`payables/?${params.toString()}`);
};

const uploadDocuments = async (data: IUploadDocuments) => {
  const files: Record<string, any> = {};
  data.documents?.forEach((file, index) => {
    files[`file-${index}`] = file;
  });

  data.documents = undefined;

  return documentsSvc.upload({ ...data, ...files });
};

const activateAccount = async (userId: string) =>
  client.get(`email-confirmation/?uuid=${userId}`);

const getClientBalances = async (): Promise<IBalancesResponse[]> =>
  client.get('balances/', {
    isAuthRedirect: false,
  });

const getPaymentDetail = async ({
  identify,
  origin,
}: {
  identify: string;
  origin: string;
}): Promise<PaymentDetailResponse> =>
  client.get(`payment/?search=${identify}&origin=${origin}`);

// TODO deprecated
const getPaymentPDF = async ({
  identify,
  origin,
}: {
  identify: string;
  origin: string;
}): Promise<any> =>
  client.get(`invoice-dashboard/?invoice=${identify}&origin=${origin}`);

const getPaymentReceipt = async ({
  identify,
  enterpriseId,
  language,
}: {
  identify: string;
  enterpriseId: string;
  language: string;
}): Promise<any> =>
  client.get(
    `enterprise/${enterpriseId}/payments/${identify}/receipt?language=${language}`
  );

const getWallet = async ({
  enterpriseId,
}: {
  enterpriseId: string;
}): Promise<WalletResponse[]> => client.get(`balance/${enterpriseId}/wallet`);

const getCombinedBalance = async (
  enterpriseId: string
): Promise<CombinedBalanceResponse> =>
  client.get(`balance/${enterpriseId}/combined`);

const convertWalletCurrencies = async ({
  enterpriseId,
  rateUuid,
  amount,
  currency,
  desCurrency,
  reverse,
}: WalletConversion): Promise<WalletResponse[]> => {
  const payload = { rateUuid, amount, currency, desCurrency, reverse };
  return client.post(`balance/${enterpriseId}/wallet-conversion`, payload, {
    isAuthRedirect: false,
  });
};

const getPaymentsBalance = (
  enterpriseId: string
): Promise<IPaymentsBalanceResponse> =>
  client.get(`charts/${enterpriseId}/wallets`);

const updateStakeHolder = async (
  data: UploadStaStakeHolders,
  enterpriseId: string
): Promise<any> => {
  const { id, ...rest } = data;
  if (!enterpriseId) {
    return Promise.reject(new Error('No enterprise Id provided'));
  }
  if (!id) {
    return Promise.reject(new Error('No id provided'));
  }
  return client.put(`enterprise/${enterpriseId}/stake-holders/${id}/`, rest, {
    isAuthRedirect: false,
  });
};

const deleteStakeHolder = (id: string | number): Promise<any> =>
  client.delete(`stake-holders/${id}/`, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const getStakeHolder = (): Promise<IStakeholdersPartials> =>
  client.get('stake-holders/', {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const createContact = async ({
  values: data,
  enterpriseId,
}: {
  values: CreateContactPayload;
  enterpriseId: string;
}): Promise<unknown> =>
  client.post(`/enterprise/${enterpriseId}/contacts/`, data, {
    isAuthRedirect: false,
  });

const updateContact = async ({
  values: data,
  enterpriseId,
  contactId,
}: {
  values: CreateContactPayload;
  enterpriseId: string;
  contactId: string;
}): Promise<unknown> => {
  const { id, ...rest } = data;
  return client.patch(
    `/enterprise/${enterpriseId}/contacts/${contactId}/`,
    rest,
    {
      isAuthRedirect: false,
    }
  );
};

const deleteContact = async ({
  contactId,
  enterpriseId,
}: {
  contactId: string;
  enterpriseId: string;
}): Promise<unknown> =>
  client.delete(`/enterprise/${enterpriseId}/contacts/${contactId}/`);

const resendActivationEmail = async ({
  contactId,
}: {
  contactId: number;
}): Promise<unknown> => client.post(`/contact/${contactId}/resend-token/`);

const getSwiftCountries = (
  searchQuery?: string
): Promise<SwiftCountriesResult[]> => {
  const params = `?search=${searchQuery ?? ''}`;
  return client.get(`banks/swift-country/${params}`);
};

const deleteUserAccountRequest = (): Promise<any> =>
  client.get('user/close-account/');

const onboardingMx = async (
  enterpriseId: number,
  data: Omit<MxOnboardingRequest, 'phoneNumber'>,
  stage: 'documents' | 'account' | 'final' | 'final_questions'
): Promise<any> =>
  client.post(`enterprise/${enterpriseId}/onboarding/?stage=${stage}`, data);

const getOnboardingMx = async (
  enterpriseId: number
): Promise<MexicoOnboardingGet> =>
  client.get(`enterprise/${enterpriseId}/onboarding/`);

const getContactCurrency = async ({
  enterpriseId,
  contactNpEnterpriseId,
}: GetContactCurrencyRequest): Promise<GetContactCurrencyResponse[]> =>
  client.get(
    `enterprise/${enterpriseId}/payment/${contactNpEnterpriseId}/currency `
  );

const validateRoutingNumber = async ({
  routing_number,
  payment_type,
}: {
  routing_number: string;
  payment_type: string;
}) => client.post('validate-routing-number/', { routing_number, payment_type });

const createStakeHolderV2 = async ({
  enterpriseID,
  values,
}: ICreateStakeholderV2) =>
  client.post(`enterprise/${enterpriseID}/stake-holders/`, values);

const getStakeHolderV2 = ({
  enterpriseId,
}: IStakeholderRequest): Promise<IStakeholderResponse> =>
  client.get(`enterprise/${enterpriseId}/stake-holders/`);

const payForward = ({
  enterpriseId,
  forwardId,
  values,
}: {
  enterpriseId: string;
  forwardId: string;
  values: ForwardPayload;
}): Promise<any> =>
  client.post(
    `/enterprise/${enterpriseId}/payments/${forwardId}/partial-payment/`,
    values
  );

const clientSvc = {
  getClients,
  getBalance,
  createClientFromExisting,
  uploadKybDocument,
  generateTransfer,
  getPayables,
  uploadDocuments,
  activateAccount,
  postUploadKybDocument,
  postNewUploadKybDocument,
  getNewUploadKybDocument,
  getClientsModal,
  getClientBalances,
  getPaymentDetail,
  validateContactEmail,
  searchContactByEmail,
  getWallet,
  getCombinedBalance,
  convertWalletCurrencies,
  getPaymentsBalance,
  generateScheduleTransfer,
  updateStakeHolder,
  deleteStakeHolder,
  getStakeHolder,
  getPaymentPDF,
  getPaymentReceipt,
  createContact,
  getSwiftCountries,
  generateSwiftTransfer,
  getContacts,
  updateContact,
  deleteContact,
  resendActivationEmail,
  deleteUserAccountRequest,
  onboardingMx,
  getOnboardingMx,
  searchContactsV2ByEmail,
  getContactCurrency,
  validateRoutingNumber,
  createStakeHolderV2,
  getStakeHolderV2,
  payForward,
};

export default clientSvc;
